import ContentSection from "../ContentSection";
import type { VoidComponent } from "solid-js";
import { createMemo, For, getOwner, runWithOwner, Show } from "solid-js";
import { useTranslation } from "../../../state/i18n";
import { TextAndIconButton } from "../../../components/button/Button";
import { MAX_INTEGRATIONS } from "../../../constants";
import InfoButton from "../../../components/infoButton/InfoButton";
import TextLink from "../../../components/link/TextLink";
import { Translation } from "../../../utils/Translation";
import { isEmpty, isNil, negate } from "lodash-es";
import IntegrationId from "../../../integrations/IntegrationId";
import type { Id } from "@thisbeyond/solid-dnd";
import { Menu } from "@ark-ui/solid";
import IntegrationShowEvent from "../../../events/IntegrationShowEvent";
import { integrationsByType } from "../../../integrations/integrations";
import InlineMessage from "../../../components/inlineMessage/InlineMessage";
import { DragAndDropContainer } from "../../../integrations/dnd/DragAndDropContainer";
import DeleteIntegration from "../../../integrations/definitions/DeleteIntegration";
import { useFormServices } from "../../../services/formServices";
import type { Integration, IntegrationType } from "../../../integrations/types";
import FormEditorDragDropProvider from "../FormEditorDragDropProvider";
import WebDAVIntegration from "../../../integrations/definitions/WebDAVIntegration";
import MongoDBIntegration from "../../../integrations/definitions/MongoDBIntegration";
import GoogleSheetsIntegration from "../../../integrations/definitions/GoogleSheetsIntegration";
import OneDriveIntegration from "../../../integrations/definitions/OneDriveIntegration";
import TaskIntegration from "../../../integrations/definitions/TaskIntegration";
import ExcelIntegration from "../../../integrations/definitions/ExcelIntegration";
import FTPSIntegration from "../../../integrations/definitions/FTPSIntegration";
import GoogleSheetsCellUpdateIntegration from "../../../integrations/definitions/GoogleSheetsCellUpdateIntegration";
import WordIntegration from "../../../integrations/definitions/WordIntegration";
import FeatureService from "../../../services/FeatureService";
import withErrorBoundary from "../../../utils/withErrorBoundary";
import EmptyState from "../../../components/emptyState/EmptyState";
import IconTileListItem from "../../../components/menu/listItems/IconTileListItem";
import { keyFromSelector } from "i18next";

type IntegrationsContentProps = {
  contentContainer: HTMLDivElement;
};

const isEmailIntegration = (integration: Integration) => integration.type === "mail";
const toMovableItem = (integration: Integration) => {
  const definition = integrationsByType[integration.type];

  return {
    id: integration.id,
    name: integration.name,
    title: definition.titleKey,
    Preview: definition.PreviewItem,
    Overlay: definition.OverlayItem,
  };
};

const IntegrationsContent: VoidComponent<IntegrationsContentProps> = (props) => {
  const { t } = useTranslation();
  const { integrationService } = useFormServices();
  const owner = getOwner();

  const allIntegrations = createMemo(() => integrationService.getIntegrations());
  const emailIntegrations = createMemo(() => allIntegrations().filter(isEmailIntegration).map(toMovableItem));
  const otherIntegrations = createMemo(() => allIntegrations().filter(negate(isEmailIntegration)).map(toMovableItem));

  const onDrop = (draggableId: Id, droppableId: Id) => {
    const integrationId = IntegrationId.from(draggableId.toString());

    if (droppableId === "last") {
      integrationService.moveIntegrationToEnd(integrationId);
    } else {
      integrationService.moveIntegrationBefore(integrationId, IntegrationId.from(droppableId.toString()));
    }
  };

  const addIntegration = (type: IntegrationType) => {
    runWithOwner(owner, () => {
      const integration = integrationService.createIntegration(type);
      integrationService.addIntegration(integration);
      new IntegrationShowEvent(integration.id).send();
    });
  };

  return (
    <div>
      <div class="flex flex-col gap-10">
        <ContentSection>
          <section aria-labelledby="mails-title" class="flex flex-col gap-4">
            <header class="flex items-center">
              <h2 id="mails-title" class="text-header-lg text-primary">
                {t(($) => $.EMAILS_TITLE)}
              </h2>
              <InfoButton
                title={t(($) => $.EMAILS_TITLE)}
                classList={{ "ml-auto": true }}
                href={t(($) => $.EMAIL_INFO_LINK)}
                ariaLabel={t(($) => $.EMAILS_INFO_ARIA_LABEL)}
                description={t(($) => $.EMAILS_INFO_ARIA_DESCRIPTION)}
              />
            </header>

            <article aria-label={t(($) => $.EMAILS_LIST_ARIA_LABEL)}>
              <Show
                when={!isEmpty(emailIntegrations())}
                fallback={
                  <EmptyState
                    illustration={{ primaryIcon: "mail" }}
                    title={t(($) => $.EMAILS_EMPTY_STATE_TITLE)}
                    description={t(($) => $.EMAILS_EMPTY_STATE_DESCRIPTION)}
                  />
                }
              >
                <FormEditorDragDropProvider>
                  <DragAndDropContainer
                    type="email-integrations"
                    contentContainer={props.contentContainer}
                    items={emailIntegrations()}
                    onDrop={onDrop}
                  />
                </FormEditorDragDropProvider>
              </Show>
            </article>

            <Show
              when={allIntegrations().length < MAX_INTEGRATIONS}
              fallback={
                <InlineMessage
                  variant="warning"
                  label={t(($) => $.INTEGRATIONS_LIMIT_REACHED, { limit: MAX_INTEGRATIONS })}
                />
              }
            >
              <div classList={{ "mx-auto -mt-6 mb-6": isEmpty(emailIntegrations()) }}>
                <AddEmailIntegrationButton addIntegration={addIntegration} />
              </div>
            </Show>
          </section>
        </ContentSection>

        <Show when={FeatureService.hasFeature("HOOKS")}>
          <ContentSection>
            <section aria-labelledby="integrations-title" class="flex flex-col gap-4">
              <header class="flex items-center">
                <h2 id="integrations-title" class="text-header-lg text-primary">
                  {t(($) => $.INTEGRATIONS_TITLE)}
                </h2>
                <InfoButton
                  title={t(($) => $.INTEGRATIONS_TITLE)}
                  classList={{ "ml-auto": true }}
                  href={t(($) => $.INTEGRATIONS_INFO_LINK)}
                  ariaLabel={t(($) => $.INTEGRATIONS_INFO_ARIA_LABEL)}
                  description={t(($) => $.INTEGRATIONS_INFO_ARIA_DESCRIPTION)}
                />
              </header>

              <article aria-label={t(($) => $.INTEGRATIONS_LIST_ARIA_LABEL)}>
                <Show
                  when={!isEmpty(otherIntegrations())}
                  fallback={
                    <EmptyState
                      illustration={{ primaryIcon: "puzzle" }}
                      title={t(($) => $.INTEGRATIONS_EMPTY_STATE_TITLE)}
                      description={t(($) => $.INTEGRATIONS_EMPTY_STATE_DESCRIPTION)}
                    />
                  }
                >
                  <FormEditorDragDropProvider>
                    <DragAndDropContainer
                      type="integrations"
                      contentContainer={props.contentContainer}
                      items={otherIntegrations()}
                      onDrop={onDrop}
                    />
                  </FormEditorDragDropProvider>
                </Show>
              </article>

              <Show
                when={allIntegrations().length < MAX_INTEGRATIONS}
                fallback={
                  <InlineMessage
                    variant="warning"
                    label={t(($) => $.INTEGRATIONS_LIMIT_REACHED, { limit: MAX_INTEGRATIONS })}
                  />
                }
              >
                <div classList={{ "mx-auto -mt-6 mb-6": isEmpty(otherIntegrations()) }}>
                  <AddIntegrationButton addIntegration={addIntegration} />
                </div>
              </Show>
            </section>
          </ContentSection>
        </Show>
      </div>

      <Show when={FeatureService.hasFeature("HOOKS")}>
        <p class="mx-auto mt-6 w-72 text-center text-base-regular text-secondary">
          <Translation
            key={keyFromSelector(($) => $.INTEGRATIONS_ALTERNATIVES)}
            components={{
              externalLink: (props) => <TextLink isExternal href={props.href} label={props.label} />,
            }}
          />
        </p>
      </Show>
    </div>
  );
};

type AddIntegrationButtonProps = {
  addIntegration: (type: IntegrationType) => void;
};
const AddEmailIntegrationButton: VoidComponent<AddIntegrationButtonProps> = (props) => {
  const { t } = useTranslation();

  return (
    <TextAndIconButton
      classList={{ "w-fit": true }}
      size="large"
      variant="light"
      icon="plus"
      aria-label={t(($) => $.EMAILS_CREATE_BUTTON)}
      text={t(($) => $.EMAILS_CREATE_BUTTON)}
      onClick={() => {
        props.addIntegration("mail");
      }}
    />
  );
};

const AddIntegrationButton: VoidComponent<AddIntegrationButtonProps> = (props) => {
  const { t } = useTranslation();
  const { integrationService } = useFormServices();

  const integrationDefinitions = createMemo(() =>
    [
      WordIntegration,
      ExcelIntegration,
      TaskIntegration,
      GoogleSheetsIntegration,
      GoogleSheetsCellUpdateIntegration,
      OneDriveIntegration,
      DeleteIntegration,
      FTPSIntegration,
      MongoDBIntegration,
      WebDAVIntegration,
    ].filter((definition) => {
      if (!isNil(definition.limit)) {
        return integrationService.getIntegrationsByType(definition.type).length < definition.limit;
      }

      return true;
    }),
  );

  return (
    <Menu.Root
      onSelect={({ value }) => {
        props.addIntegration(value as IntegrationType);
      }}
    >
      <Menu.Trigger
        asChild={(props) => (
          <TextAndIconButton
            {...props}
            classList={{ "w-fit": true }}
            size="large"
            variant="light"
            icon="plus"
            aria-label={t(($) => $.INTEGRATIONS_CREATE_BUTTON)}
            text={t(($) => $.INTEGRATIONS_CREATE_BUTTON)}
          />
        )}
      />
      <Menu.Positioner>
        <Menu.Content class="z-10 -my-1 max-h-86 min-h-12 max-w-170 min-w-66 overflow-y-auto rounded-sm bg-surface-default p-1 shadow-elevation-light-3 outline-none">
          <For each={integrationDefinitions()}>
            {(integration) => (
              <Menu.Item
                value={integration.type}
                asChild={(arkProps) => (
                  <IconTileListItem
                    label={t(integration.titleKey)}
                    icon={integration.icon}
                    description={t(integration.descriptionKey)}
                    htmlProps={arkProps()}
                  />
                )}
              />
            )}
          </For>
        </Menu.Content>
      </Menu.Positioner>
    </Menu.Root>
  );
};

const IntegrationsContentWithErrorBoundary = withErrorBoundary(IntegrationsContent);

export default IntegrationsContentWithErrorBoundary;
